import { Flex, Text } from '@chakra-ui/react'
import { Link } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { QuickActionButtonVariants } from '~/components/shared/componentStyles/QuickActionButton.style'
import { DateTimeRangeDropdown } from '~/components/shared/DateTimeRangeDropdown'
import { QuickActionButton } from '~/components/shared/QuickActionButton'
import { SearchInput } from '~/components/shared/SearchInput'
import { useToastAlert } from '~/hooks/toast/useToastAlerts'
import { useCheckRole } from '~/hooks/useCheckRole'
import { useClipboard } from '~/hooks/useClipboard'
import { CallsFilterButton } from '~/pages/Calls/CallsFilterButton'
import { ExportCallsCsvButton } from '~/pages/Calls/ExportCallsCsvButton'
import { Environment } from '~/types/EnvironmentTypes'
import { DateRange, SearchValue, SelectedFilterData } from '~/types/Shared'
import { Size } from '~/types/ThemeTypes'
import { appEnvironment } from '~/utils/common'
import { getShareableWebAppUrl } from '~/utils/shareableUrl'

import { textStyleKeys } from '@mangovoice/design-system'
import { textStyles } from '@mangovoice/design-system'

import { useCallsViewModelContext } from './Calls.viewmodel'

interface CallsListViewHeaderProps {
  searchValue?: SearchValue
  dateTimeRange?: DateRange
  callCount?: number
  tableSize?: Size
  onActiveFiltersChanged?: (activeFilters: SelectedFilterData[]) => void
  onDateTimeRangeChanged?: (dateTimeRange: DateRange) => void
  onSearchValueChanged?: (searchValue: SearchValue) => void
}

export const CallsListViewHeader = ({
  searchValue,
  dateTimeRange,
  tableSize,
  onActiveFiltersChanged,
  onDateTimeRangeChanged,
  onSearchValueChanged,
}: CallsListViewHeaderProps) => {
  const { t } = useTranslation()
  const { toastSuccess, toastError } = useToastAlert()
  const { state } = useCallsViewModelContext()
  const checkRole = useCheckRole()
  const isAdmin = checkRole('admin')
  const { copyToClipboard } = useClipboard({
    onSuccess: () => toastSuccess(t('callDetails.toast.linkCopied')),
    onFailure: () => toastError(t('callDetails.toast.linkCopyFailed')),
  })

  const handleCopyLink = () => {
    try {
      copyToClipboard(getShareableWebAppUrl())
    } catch (error) {
      console.error(error)
    }
  }

  return (
    <Flex
      minHeight="4.5rem"
      height="4.5rem"
      px="1.25rem"
      alignItems="center"
      gap="0.75rem"
      alignSelf="stretch"
      borderBottom="1px solid"
      bg="bg.default"
      borderColor="border.muted"
    >
      <Flex
        data-testid="input-wrapper"
        alignItems="center"
        gap="0.75rem"
        flex="1"
      >
        <SearchInput
          value={searchValue?.label ?? ''}
          placeholder={t('form.placeholder.searchNameOrNumber')}
          inputLabel={t('calls.searchLabel', 'Search calls')}
          maxWidth="25rem"
          gap="0.5rem"
          flex="1 0 0"
          onChange={onSearchValueChanged}
        />
        <DateTimeRangeDropdown
          onValueChange={onDateTimeRangeChanged}
          useQuickActionButton={tableSize === 'sm'}
          dateTimeRange={dateTimeRange}
        />
        <CallsFilterButton
          onActiveFiltersChanged={onActiveFiltersChanged}
          useQuickActionButton={tableSize === 'sm'}
        />
        <Text {...textStyles[textStyleKeys.Body2]} color="fg.default">
          {state?.totalFilteredCallCount ?? 0} {t('nav.calls')}
        </Text>
      </Flex>
      <Flex alignItems="flex-end" gap="0.5rem">
        {isAdmin && appEnvironment !== Environment.PRODUCTION_UK && (
          <ExportCallsCsvButton />
        )}
        <QuickActionButton
          variant={QuickActionButtonVariants.Default_200}
          tooltipText={t('callDetails.tooltip.copyLinkToSearch')}
          actionIcon={Link}
          onClick={() => handleCopyLink()}
        />
      </Flex>
    </Flex>
  )
}
